import { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  LoaderCircle,
  Maximize2,
  Minimize2,
  RotateCcw,
} from "lucide-react";
import FieldLayout, { FieldErrors } from "components/field/FieldLayout";
import { BatteryBanner, usePower } from "components/field/BatteryRunway";
import FieldDiagnostics from "components/field/FieldDiagnostics";
import CalibrationEditor from "components/field/CalibrationEditor";
import CaptureTransport from "components/field/CaptureTransport";
import LensView from "components/field/LensView";
import Panorama from "components/field/Panorama";
import { useCaptureFrames } from "hooks/useCaptureFrames";
import { useFieldNodes, useFieldResource } from "hooks/useFieldNodes";
import {
  useFullscreen,
  useViewerKeys,
  useWheelZoom,
} from "hooks/useViewerControls";
import { rtctApi } from "services/rtctApi";
import {
  DEFAULT_SPEED,
  canPlay,
  markRange,
  nextPlaybackIndex,
  playStartIndex,
  resolveRange,
} from "utils/capturePlayback";
import {
  calibrationForIdentities,
  cleanCalibration,
  defaultCalibration,
  eligibleCaptures,
  fieldTime,
  formatBytes,
} from "utils/fieldFormat";

const NO_RANGE = { start: null, end: null };

export default function FieldCameras() {
  const nodes = useFieldNodes();
  return <FieldCamerasContent key={nodes.mac} nodes={nodes} />;
}
function FieldCamerasContent({ nodes }) {
  const [refresh, setRefresh] = useState(0);
  const [liveRefresh, setLiveRefresh] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setLiveRefresh((n) => n + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);
  const frames = useFieldResource(
      nodes.mac,
      rtctApi.frames,
      refresh + liveRefresh,
    ),
    telemetry = useFieldResource(
      nodes.mac,
      rtctApi.telemetry,
      refresh + liveRefresh,
    ),
    calibration = useFieldResource(nodes.mac, rtctApi.calibration, refresh);
  const power = usePower(nodes.mac);
  const [history, setHistory] = useState({
    captures: [], count: 0, nextCursor: null, initialized: false,
  });
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const olderRequest = useRef(null);
  useEffect(() => () => olderRequest.current?.abort(), []);
  useEffect(() => {
    if (!frames.data) return;
    setHistory((old) => {
      const merged = new Map(old.captures.map((capture) => [capture.capture_id, capture]));
      for (const capture of frames.data.captures || []) merged.set(capture.capture_id, capture);
      return {
        captures: [...merged.values()],
        count: Math.max(old.count, frames.data.count || 0),
        // Keep the older-page snapshot while polling fresh captures at the head.
        nextCursor: old.initialized && (old.nextCursor || merged.size >= (frames.data.count || 0))
          ? old.nextCursor : frames.data.next_cursor || null,
        initialized: true,
      };
    });
  }, [frames.data]);
  const loadOlder = async () => {
    if (!history.nextCursor || olderRequest.current) return;
    const abort = new AbortController();
    olderRequest.current = abort;
    setLoadingOlder(true);
    setHistoryError("");
    try {
      const page = await rtctApi.frames(nodes.mac, abort.signal, history.nextCursor);
      if (abort.signal.aborted) return;
      setHistory((old) => {
        const merged = new Map(old.captures.map((capture) => [capture.capture_id, capture]));
        for (const capture of page.captures || []) {
          if (!merged.has(capture.capture_id)) merged.set(capture.capture_id, capture);
        }
        return {
          captures: [...merged.values()],
          count: Math.max(old.count, page.count || 0),
          nextCursor: page.next_cursor || null,
          initialized: true,
        };
      });
    } catch (error) {
      if (!abort.signal.aborted) setHistoryError(error.message);
    } finally {
      if (!abort.signal.aborted) setLoadingOlder(false);
      olderRequest.current = null;
    }
  };
  const captures = useMemo(
    () => eligibleCaptures(history.captures),
    [history.captures],
  );
  const [captureId, setCaptureId] = useState(null),
    [mode, setMode] = useState("360"),
    [draft, setDraft] = useState(defaultCalibration),
    [saved, setSaved] = useState(null),
    [saveError, setSaveError] = useState(""),
    [busy, setBusy] = useState(false),
    [reset, setReset] = useState(0),
    [playing, setPlaying] = useState(false),
    [fps, setFps] = useState(DEFAULT_SPEED),
    [range, setRange] = useState(NO_RANGE);
  const dirtyRef = useRef(false);
  dirtyRef.current = !!saved && JSON.stringify(draft) !== JSON.stringify(saved);
  const currentMac = useRef(nodes.mac);
  currentMac.current = nodes.mac;
  const scope = useRef({ mac: nodes.mac, generation: 0 });
  if (scope.current.mac !== nodes.mac)
    scope.current = {
      mac: nodes.mac,
      generation: scope.current.generation + 1,
    };
  // Until a capture is chosen the newest one is shown (the effect below selects it).
  const selectedIndex = captures.findIndex((c) => c.capture_id === captureId);
  const index =
    selectedIndex >= 0 ? selectedIndex : Math.max(0, captures.length - 1);
  const capture = captures[index];
  const loop = useMemo(() => resolveRange(captures, range), [captures, range]);
  const playable = canPlay(captures.length, loop);
  const display = useCaptureFrames(captures, index, { playing, range: loop });
  const shown = display.frame;
  const identities = Object.keys(draft.cameras).sort();
  const eyeA = identities[0],
    eyeB = identities[1];
  const images = useMemo(
    () => ({ A: shown?.images[eyeA], B: shown?.images[eyeB] }),
    [shown, eyeA, eyeB],
  );
  const lensUrl =
    mode === "360" ? null : shown?.urls[mode === "A" ? eyeA : eyeB];
  const viewerCalibration = useMemo(() => {
    const ids = Object.keys(draft.cameras).sort();
    return {
      ...draft,
      cameras: {
        A: draft.cameras[ids[0]],
        B: draft.cameras[ids[1]] || defaultCalibration().cameras.B,
      },
    };
  }, [draft]);
  useEffect(() => {
    setCaptureId(null);
    setDraft(defaultCalibration());
    setSaved(null);
    setSaveError("");
    setBusy(false);
  }, [nodes.mac]);
  useEffect(() => {
    if (calibration.data) {
      const value = { ...defaultCalibration(), ...calibration.data };
      if (!dirtyRef.current) setDraft(cleanCalibration(value));
      setSaved(cleanCalibration(value));
    }
  }, [calibration.data]);
  useEffect(() => {
    setCaptureId((id) =>
      captures.some((c) => c.capture_id === id)
        ? id
        : captures[captures.length - 1]?.capture_id || null,
    );
  }, [captures]);

  // Playback: each capture gets its turn only once it is on screen, so a slow frame holds
  // playback instead of being skipped.
  useEffect(() => {
    if (!playing || !display.ready) return undefined;
    const timer = setTimeout(() => {
      const next = nextPlaybackIndex(index, captures.length, loop);
      if (next === null) setPlaying(false);
      else setCaptureId(captures[next].capture_id);
    }, 1000 / fps);
    return () => clearTimeout(timer);
  }, [playing, display.ready, fps, index, captures, loop]);
  useEffect(() => {
    if (playing && !playable) setPlaying(false);
  }, [playing, playable]);
  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if (!playable) return;
    const start = playStartIndex(index, captures.length, loop);
    if (start !== index) setCaptureId(captures[start].capture_id);
    setPlaying(true);
  };
  const step = (delta) => {
    setPlaying(false);
    const next = Math.min(captures.length - 1, Math.max(0, index + delta));
    if (next !== index && captures[next]) setCaptureId(captures[next].capture_id);
  };
  const mark = (edge) => {
    if (capture)
      setRange((r) => markRange(r, edge, capture.capture_id, captures));
  };

  const [stage, setStage] = useState(null);
  const [viewer, setViewer] = useState(null);
  const viewRef = useRef(null);
  const fullscreen = useFullscreen(stage);
  useViewerKeys(stage, {
    togglePlay,
    previous: () => step(-1),
    next: () => step(1),
    markStart: () => mark("start"),
    markEnd: () => mark("end"),
  });
  useWheelZoom(viewer, mode === "360" || !!lensUrl, (factor, x, y) =>
    viewRef.current?.zoomBy(factor, x, y),
  );

  const dirty = !!saved && JSON.stringify(draft) !== JSON.stringify(saved);
  const save = async () => {
    const mac = nodes.mac,
      generation = scope.current.generation;
    setBusy(true);
    setSaveError("");
    try {
      const result = await rtctApi.saveCalibration(
        mac,
        cleanCalibration(draft),
      );
      if (currentMac.current !== mac || scope.current.generation !== generation)
        return;
      setDraft(cleanCalibration(result));
      setSaved(cleanCalibration(result));
    } catch (e) {
      if (currentMac.current === mac && scope.current.generation === generation)
        setSaveError(
          e.status === 409
            ? "Calibration changed in another session. Refresh, then choose Discard preview to load its saved revision."
            : e.message,
        );
    } finally {
      if (currentMac.current === mac && scope.current.generation === generation)
        setBusy(false);
    }
  };
  // Playback re-renders the page several times a second; the diagnostics charts only need to
  // follow the telemetry.
  const samples = telemetry.data?.samples;
  const diagnostics = useMemo(
    () => <FieldDiagnostics samples={samples || []} />,
    [samples],
  );
  const loadingFirst = (frames.loading && !capture) || (!!capture && !shown);
  const message =
    loadingFirst || !capture || (mode !== "360" && !lensUrl)
      ? {
          title: loadingFirst
            ? "Loading field capture"
            : !capture
              ? "Your next capture starts here"
              : `Camera ${mode} frame unavailable`,
          detail: !capture
            ? "New captures appear after the node wakes and uploads. Only October onwards is retained here."
            : loadingFirst
              ? "Securely loading the original frames."
              : "The other camera and telemetry remain available.",
        }
      : null;
  return (
    <FieldLayout
      nodes={nodes}
      section="camera"
      title="Field cameras"
      subtitle="Photos from both cameras, the sensor readings and lens alignment."
      refresh={() => setRefresh((n) => n + 1)}
    >
      <FieldErrors
        errors={[
          frames.error,
          historyError,
          telemetry.error,
          calibration.error,
          display.error,
          saveError,
        ]}
      />
      <BatteryBanner power={power} />
      <div className="field-camera-grid">
        <section className="field-panel">
          <div className="field-panel-head">
            <div>
              <h2>{nodes.node?.name || "Field node"} · spatial view</h2>
              <p>
                {capture
                  ? `${fieldTime(capture.captured_at)} SGT · ${capture.cameras.length} camera frame${capture.cameras.length === 1 ? "" : "s"}`
                  : "Captures from 1 October 2026 onwards"}
              </p>
            </div>
            <Camera size={19} color="#6c9296" />
          </div>
          <div
            ref={setStage}
            className={`field-stage${fullscreen.active ? " is-fullscreen" : ""}`}
            role="region"
            aria-label="Capture viewer"
          >
            <div className="field-view-controls">
              <div className="field-segment">
                {["360", "A", "B"].map((m) => (
                  <button
                    className={mode === m ? "active" : ""}
                    aria-pressed={mode === m}
                    key={m}
                    onClick={() => setMode(m)}
                  >
                    {m === "360" ? "360° view" : `Camera ${m}`}
                  </button>
                ))}
              </div>
              <div className="field-panorama-actions">
                <button
                  className="field-button"
                  aria-label="Reset view"
                  title="Reset view"
                  onClick={() => setReset((n) => n + 1)}
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  className="field-button"
                  aria-label={fullscreen.active ? "Exit fullscreen" : "Fullscreen viewer"}
                  title={fullscreen.active ? "Exit fullscreen (Esc)" : "Fullscreen viewer"}
                  onClick={fullscreen.toggle}
                >
                  {fullscreen.active ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
              </div>
            </div>
            <div ref={setViewer} className="field-viewer" data-testid="capture-viewer">
              {mode === "360" ? (
                <Panorama
                  images={images}
                  calibration={viewerCalibration}
                  resetKey={reset}
                  viewRef={viewRef}
                />
              ) : (
                <LensView
                  key={mode}
                  src={lensUrl || null}
                  alt={
                    shown
                      ? `Camera ${mode} capture at ${fieldTime(shown.capture.captured_at)}`
                      : `Camera ${mode}`
                  }
                  resetKey={reset}
                  viewRef={viewRef}
                  describedBy="panorama-instructions"
                />
              )}
              {message && (
                <div className="field-viewer-message" role="status">
                  <Camera size={34} />
                  <strong>{message.title}</strong>
                  <span>{message.detail}</span>
                </div>
              )}
              {display.slow && shown && (
                <span className="field-loading-badge" role="status">
                  <LoaderCircle size={13} className="field-spin" aria-hidden="true" />
                  Loading capture
                </span>
              )}
              {shown && (
                <div className="field-overlay">
                  {shown.capture.cameras.map((c) => (
                    <span className="field-badge" key={c.id}>
                      {c.camera} · {c.width} × {c.height} · {formatBytes(c.bytes)}
                      {c.expired ? " · retained thumbnail" : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <CaptureTransport
              captures={captures}
              index={index}
              total={history.count}
              onSeek={(i) => captures[i] && setCaptureId(captures[i].capture_id)}
              playing={playing}
              canPlay={playable}
              onTogglePlay={togglePlay}
              onStep={step}
              fps={fps}
              onFps={setFps}
              range={loop}
              onMark={mark}
              onClearRange={() => setRange(NO_RANGE)}
            />
          </div>
          <div className="field-timeline">
            {history.nextCursor && (
              <button
                className="field-button"
                aria-label="Load older captures"
                aria-busy={loadingOlder}
                disabled={loadingOlder}
                onClick={loadOlder}
              >
                {loadingOlder ? "Loading older captures…" : "Load older captures"}
              </button>
            )}
            <p id="panorama-instructions" className="field-caption">
              Drag the panorama to look around (or Shift + arrow keys); scroll,
              pinch or + / − to zoom; zoomed single-camera views pan by
              dragging. Space plays or pauses, ← / → step one capture, [ and ]
              mark the loop start and end. Capture pairs share one capture
              identity; missing frames remain explicit. Camera A:{" "}
              {identities[0] || "unreported"}; Camera B:{" "}
              {identities[1] || "unreported"}.
            </p>
          </div>
        </section>
        <CalibrationEditor
          value={draft}
          onChange={setDraft}
          onDiscard={() => setDraft(saved)}
          onSave={save}
          onReset={() =>
            setDraft({
              ...calibrationForIdentities(Object.keys(saved.cameras).sort()),
              revision: saved.revision,
            })
          }
          busy={busy}
          dirty={dirty}
          revision={saved?.revision}
          disabled={!saved}
        />
      </div>
      {diagnostics}
    </FieldLayout>
  );
}
